import SortableTable from 'app/agriculture/components/Table/SortableTable';
import { Device } from 'app/agriculture/queries/devices/types/Device';
import { useMemo } from 'react';
import { Button } from 'app/agriculture/components/Button/Button';
import { CircleIcon } from 'app/agriculture/icons';
import { LinkedEntityList } from 'app/agriculture/components/LinkedEntityList/LinkedEntityList';
import { DeviceDetailContext } from 'app/agriculture/routes/Devices/DeviceDetails/context/DeviceDetailContextProvider';
import { SuspenseWrapper } from 'app/agriculture/components/SuspenseWrapper/SuspenseWrapper';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { Skeleton } from '@mui/material';
import { DeviceStatusProvider } from 'app/agriculture/routes/Devices/utils/status/DeviceStatusContext';
import { StatHealth } from 'app/agriculture/components/StatHealth/StatHealth';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { DeviceFlyToButton } from 'app/agriculture/routes/Devices/components/DeviceFlyToButton';
import { useGenericEntityFromId } from 'app/agriculture/queries/entities/useGenericEntityFromId';
import { useGetLevelSnapshot } from 'app/agriculture/routes/Devices/LevelMonitorList/hooks/useGetLevelSnapshot';

interface LevelMonitorListTableProps {
    devices: Device[];
    disableSort?: boolean;
    onSort?: () => void;
}

export function LevelMonitorListTable({
    devices,
    disableSort = false,
    onSort = () => {
        /* do nothing */
    },
}: LevelMonitorListTableProps) {
    const routeActions = useRouteActions();
    const getParent = useGenericEntityFromId();
    const getLevelSnapshot = useGetLevelSnapshot();

    const deviceData = useMemo(
        () =>
            devices.map((device) => {
                const { id, name } = device;
                const levelSnapshot = getLevelSnapshot(device);
                const tank = getParent(device.infrastructureId);

                return {
                    id,
                    name: {
                        value: name,
                        display: (
                            <div className="LevelMonitorList__name">
                                <SuspenseWrapper loadingComponent={null}>
                                    <DeviceFlyToButton deviceId={id} />
                                </SuspenseWrapper>
                                <Button
                                    variant="compact"
                                    onClick={() => {
                                        routeActions.goto(id, { type: 'Device' });
                                    }}
                                    text={name}
                                    className="LevelMonitorList__button"
                                />
                            </div>
                        ),
                    },
                    level: {
                        value: levelSnapshot?.percentageRemaining ?? '--',
                        display: `${levelSnapshot?.percentageRemaining ?? '--'}%`,
                    },
                    tank: {
                        value: tank?.name ?? '--',
                        display:
                            tank?.id == null ? '--' : <LinkedEntityList entityIds={[tank.id]} />,
                    },
                    status: {
                        value: '',
                        display: (
                            <DeviceDetailContext.Provider value={{ device }}>
                                <SuspenseWrapper
                                    loadingComponent={
                                        <StatDisplay
                                            Icon={(props) => (
                                                <CircleIcon {...props} size={10} color="grey" />
                                            )}
                                            value={<Skeleton variant="text" />}
                                        />
                                    }
                                >
                                    <DeviceStatusProvider deviceId={device.id}>
                                        <StatHealth hideLabel />
                                    </DeviceStatusProvider>
                                </SuspenseWrapper>
                            </DeviceDetailContext.Provider>
                        ),
                    },
                };
            }),
        [getParent, devices, getLevelSnapshot, routeActions],
    );

    return (
        <SortableTable
            button={({ id }) => ({
                text: 'Edit',
                onClick: () => {
                    routeActions.edit(id, { type: 'Device' });
                },
                startIcon: 'edit',
                variant: 'compact',
            })}
            data={deviceData}
            headCells={[
                { key: 'name', label: 'Name' },
                { key: 'level', label: 'Level' },
                { key: 'tank', label: 'Tank' },
                { key: 'status', label: 'Status' },
            ]}
            defaultSort={{ key: 'name', order: 'asc' }}
            disableSort={disableSort}
            onSort={onSort}
        />
    );
}
